import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '../lib/api'
import type {
  User,
  UserProfile,
  AIPersona,
  PersonaConversation,
  Match,
  Message,
  Subscription,
  Credit,
  MyPersona,
  MatchPreferences,
  UserPoints,
} from './types'

interface AppState {
  // Auth
  user: User | null
  token: string | null
  setUser: (user: User | null) => void
  setToken: (token: string | null) => void
  logout: () => void

  // Profile
  profile: Partial<UserProfile> | null
  setProfile: (profile: Partial<UserProfile> | null) => void

  // AI Personas
  personas: AIPersona[]
  setPersonas: (personas: AIPersona[]) => void
  addPersona: (persona: AIPersona) => void
  updatePersona: (id: number, updates: Partial<AIPersona>) => void
  removePersona: (id: number) => void

  // Conversations
  conversations: PersonaConversation[]
  setConversations: (conversations: PersonaConversation[]) => void
  addConversation: (conversation: PersonaConversation) => void
  clearConversations: (personaId: number) => void

  // Matching
  matches: Match[]
  setMatches: (matches: Match[]) => void
  addMatch: (match: Match) => void
  updateMatch: (id: number, updates: Partial<Match>) => void
  matchPreferences: MatchPreferences | null
  setMatchPreferences: (prefs: MatchPreferences | null) => void

  // Messages
  messages: Message[]
  setMessages: (messages: Message[]) => void
  addMessage: (message: Message) => void
  translateMessage: (messageId: number, targetLocale: string) => Promise<{ translatedText: string; cultureHint: string | null } | undefined>

  // Subscription & Credits
  subscription: Subscription | null
  setSubscription: (subscription: Subscription | null) => void
  credits: Credit | null
  setCredits: (credits: Credit | null) => void

  // My Persona (AI 분신)
  myPersona: MyPersona | null
  setMyPersona: (myPersona: MyPersona | null) => void

  // Points
  points: UserPoints | null
  setPoints: (points: UserPoints | null) => void
  fetchPoints: () => Promise<void>

  // UI State
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Auth
      user: null,
      token: null,
      setUser: (user) => set({ user }),
      setToken: (token) => {
        api.setToken(token)
        set({ token })
      },
      logout: () =>
        set({
          user: null,
          token: null,
          profile: null,
          personas: [],
          conversations: [],
          matches: [],
          messages: [],
          subscription: null,
          credits: null,
          myPersona: null,
          matchPreferences: null,
          points: null,
        }),

      // Profile
      profile: null,
      setProfile: (profile) => set({ profile }),

      // AI Personas
      personas: [],
      setPersonas: (personas) => set({ personas }),
      addPersona: (persona) =>
        set((state) => ({ personas: [...state.personas, persona] })),
      updatePersona: (id, updates) =>
        set((state) => ({
          personas: state.personas.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          ),
        })),
      removePersona: (id) =>
        set((state) => ({
          personas: state.personas.filter((p) => p.id !== id),
        })),

      // Conversations
      conversations: [],
      setConversations: (conversations) => set({ conversations }),
      addConversation: (conversation) =>
        set((state) => ({
          conversations: [...state.conversations, conversation],
        })),
      clearConversations: (personaId) =>
        set((state) => ({
          conversations: state.conversations.filter(
            (c) => c.persona_id !== personaId
          ),
        })),

      // Matching
      matches: [],
      setMatches: (matches) => set({ matches }),
      addMatch: (match) =>
        set((state) => ({ matches: [...state.matches, match] })),
      updateMatch: (id, updates) =>
        set((state) => ({
          matches: state.matches.map((m) =>
            m.id === id ? { ...m, ...updates } : m
          ),
        })),
      matchPreferences: null,
      setMatchPreferences: (prefs) => set({ matchPreferences: prefs }),

      // Messages
      messages: [],
      setMessages: (messages) => set({ messages }),
      addMessage: (message) =>
        set((state) => ({ messages: [...state.messages, message] })),
      translateMessage: async (messageId, targetLocale) => {
        const state = get()
        const message = state.messages.find((m) => m.id === messageId)
        if (!message) return

        // Auto-detect: user locale > browser language > 'ko'
        const browserLang = typeof navigator !== 'undefined' && navigator.language
          ? navigator.language.split('-')[0]
          : null
        const finalTargetLocale = targetLocale || browserLang || 'ko'

        console.log(`[Translation] ${message.original_locale} -> ${finalTargetLocale}`)

        try {
          const { translateWithFallback } = await import('../lib/browserTranslation')
          const result = await translateWithFallback(
            message.content,
            message.original_locale || 'en',
            finalTargetLocale
          )

          console.log(`[Translation] Method: ${result.method}`)

          set((state) => ({
            messages: state.messages.map((m) =>
              m.id === messageId
                ? {
                  ...m,
                  content_translated: result.translatedText,
                  culture_hint: result.cultureHint,
                }
                : m
            ),
          }))

          return {
            translatedText: result.translatedText,
            cultureHint: result.cultureHint
          }
        } catch (error) {
          console.error('[Translation] Error:', error)
          return undefined
        }
      },

      // Subscription & Credits
      subscription: null,
      setSubscription: (subscription) => set({ subscription }),
      credits: null,
      setCredits: (credits) => set({ credits }),

      // My Persona (AI 분신)
      myPersona: null,
      setMyPersona: (myPersona) => set({ myPersona }),

      // Points
      points: null,
      setPoints: (points) => set({ points }),
      fetchPoints: async () => {
        try {
          const response = await api.points.getPoints()
          if (response.success) {
            set({ points: response.points })
          }
        } catch (error) {
          console.error('Failed to fetch points:', error)
        }
      },

      // UI State
      isLoading: false,
      setIsLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'glovely-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        profile: state.profile,
        personas: state.personas,
        conversations: state.conversations,
        myPersona: state.myPersona,
        points: state.points,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          api.setToken(state.token)
        }
      },
    }
  )
)
